import catalogJson from '../../catalog.json'
import { catalogFileSchema } from '../catalog.schema'
import type { CatalogCostume, CatalogFile, CatalogRide } from '../catalog.schema'
import { ConfigError, formatIssues } from '../errors'
import { RIDE_IDS } from '../rides.schema'
import type { RideId } from '../rides.schema'

/**
 * The cosmetics catalogue (D-049), importable alone as `@runkr/config/catalog` so the client
 * menu never pulls the balance/track loader (like `@runkr/config/economy`).
 */
export {
  COSTUME_SLOTS,
  CURRENCIES,
  RARITIES,
  catalogCostumeSchema,
  catalogFileSchema,
  catalogRideSchema,
} from '../catalog.schema'
export type {
  CatalogCostume,
  CatalogFile,
  CatalogRide,
  CostumeSlot,
  Rarity,
} from '../catalog.schema'
export { RIDE_IDS } from '../rides.schema'
export type { RideId } from '../rides.schema'

/** Validated `catalog.json`. */
export interface Catalog {
  /** In display order (the hub's grid, the shop's Rides tab). */
  readonly rides: readonly Readonly<CatalogRide>[]
  /** The first starter ride: what a seat rides when it asks for none or an unknown one. */
  readonly defaultRide: RideId
  /** Costume items in display order (T3.8); empty until the runner cast is final. */
  readonly costumes: readonly Readonly<CatalogCostume>[]
  /** How many items the shop's Featured tab picks each UTC day (T3.9a). */
  readonly featured: number
}

/** Validate `catalog.json`; throws `ConfigError` naming the failing path. */
export function loadCatalog(raw: unknown = catalogJson): Catalog {
  const parsed = catalogFileSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ConfigError(`Invalid config\n${formatIssues('catalog', parsed.error)}`)
  }
  const f: CatalogFile = parsed.data
  const rides = Object.freeze(f.rides.map((r) => Object.freeze({ ...r })))
  const defaultRide = rides.find((r) => r.starter)!.id
  const costumes = Object.freeze(f.costumes.map((c) => Object.freeze({ ...c })))
  return Object.freeze({ rides, defaultRide, costumes, featured: f.shop.featured })
}

/** `raw` when it names a ride (`RIDE_IDS`), else `fallback`. */
export function rideOr(raw: unknown, fallback: RideId): RideId {
  return typeof raw === 'string' && (RIDE_IDS as readonly string[]).includes(raw)
    ? (raw as RideId)
    : fallback
}
